import { useCallback, useContext, useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import { LOGIN_FAIL } from '@comeon/actions-public';
import { accountUpdate, enableMentorMessageDialog } from '~/components/account/ducks';
import { WEB_EVENTS, sendAppBridgeEvent } from '~/components/app-bridge';
import { getGoogleClientId, setSessionTime } from '~/components/authentication';
import {
	bankIdResult,
	bankIdSessionLookup,
	cancelBankIDRequest,
	bankIdReinitiate
} from '~/components/authentication/bankid';
import useRefState from '~/hooks/use-ref-state';
import { selectSGADepositLimitsEnabled } from '~/components/brand/ducks/selectors';
import useConfiguration from '~/configuration/use-configuration';
import useIsMounted from '~/hooks/use-is-mounted';
import UIContext from '~/ui/ui-context';
import {
	disableValidateSession,
	showUsernameAndPasswordLogin,
	showWelcomeScreen,
	updateAcceptMarketingOffers,
	updateCredentials,
	validateSession
} from '../ducks';

import { BANKID_MODES, GUEST_FRANCHISE_MODES } from './configs';
import {
	bankIdError,
	bankIdUpdateOrderRef,
	bankIdUpdateSessionCriteria,
	clearBankID,
	getBankIDToken
} from './ducks/actions';
import {
	getBankIDAppURL,
	getBankIDRequestType,
	isSessionCriteriaFulfilled,
	postBankIDAnalytics,
	pushBankIDVerificationEvent,
	showAccountLink
} from './utils';
import { isIOSSafari } from '~/utils/user-agent-utils';
import usePrevious from '~/hooks/use-previous';
import { ROUTE_ACCOUNT_DEPOSIT } from '~/components/router/constants';

/**
 * BankID Hooks that can be reused across other componennt
 *
 * @example
 * import { useBankID } from `@comeon/authentication`;
 *


...
 *
 * const { startBankID, bankIDTransaction, updateBankIDSession, } = useBankID();
 *
 * startBankID(paylod);
 *
 * intiate bankid with three options
 * 1. empty payload, auto start bankid and get token
 * 2. personal number, get token for this personal number
 * 3. cached orderRef, skip token and initiate polling with order ref
 *
 * bankIDTransaction - returns status with success, failure or pending
 *
 * updateBankIDSession({clearBankIDOnSuccess});
 *
 * updates the session in based on the order ref
 * if session criteria is fulfilled, activates the session
 *
 * optional paramter to clear bankid state on success
 * ...
 *
 */

// poll  duration is 40 seconds
const POLL_DURATION = 40_000;
const POLL_INTERVAL = 2_000;

const useBankID = ({ mode = BANKID_MODES.LOGIN }) => {
	// local state
	const [polling, setPolling] = useState({
		endTime: 0,
		on: false,
		failure: false
	});
	const previousPollingState = usePrevious(polling.on);
	const [timer, setTimer] = useState(null);
	const [bankIDTransaction, setBankIDTransaction] = useState({
		status: 'none',
		response: null
	});
	const [pollConfig, setPollConfig] = useRefState({
		duration: POLL_DURATION,
		interval: POLL_INTERVAL
	});
	// redux states
	const { orderRef, qrMode } = useSelector(state => state.bankId);
	const { siteId, franchiseId, defaultLocale, guestFranchiseId, isSwedishRegulatedFranchise } = useSelector(
		state => state.brand
	);
	const { selectedLocale, isRegistration } = useSelector(state => state.account);
	const depositLimitsSwedishRegulationEnabled = useSelector(selectSGADepositLimitsEnabled);
	// hooks
	const { withinSidebar } = useContext(UIContext);
	const { isIntegrationTesting } = useConfiguration();
	const dispatch = useDispatch();
	const isMounted = useIsMounted();

	// update bankid state in redux after lookupSession, activate session if criteria fulfilled
	const updateBankIDSession = useCallback(
		async ({ clearBankIDOnSuccess = false } = {}) => {
			if (!orderRef) {
				return;
			}
			const result = await bankIdSessionLookup(orderRef);
			if (Object.prototype.hasOwnProperty.call(result, 'success') && isMounted.current) {
				const sessionCriteriaScreen = {
					shouldShowComplianceMessage: result.shouldShowComplianceMessage,
					shouldShowPrivacyPolicyMessage: result.shouldShowPrivacyPolicyMessage,
					shouldShowEmail: !result.isEmailSet,
					shouldShowPhone: !result.isMobileNumberSet
				};
				if (depositLimitsSwedishRegulationEnabled) {
					sessionCriteriaScreen.shouldShowDepositLimitPage = result.shouldShowDepositLimitPage;
				}
				const sessionCriteria = {
					...sessionCriteriaScreen,
					depositLimitCountryCode: result.depositLimitCountryCode,
					shouldShowWelcomePageDetails: result.shouldShowWelcomePageDetails
				};
				dispatch(bankIdUpdateSessionCriteria(sessionCriteria));
				if (Object.keys(sessionCriteriaScreen).every(k => !sessionCriteriaScreen[k])) {
					// for registrations redirect to deposit flow
					if (isRegistration) {
						dispatch(
							updateCredentials({
								postAuthLocation: {
									pathname: ROUTE_ACCOUNT_DEPOSIT
								}
							})
						);
					} else {
						dispatch(showWelcomeScreen(true));
					}
					// wait for validate session to be done before proceeding ahead
					await dispatch(validateSession({}, true));
					dispatch(enableMentorMessageDialog());
					if (!clearBankIDOnSuccess) {
						dispatch(clearBankID());
					}
					return true;
				}
			}
			return false;
		},
		[dispatch, depositLimitsSwedishRegulationEnabled, orderRef, isRegistration, isMounted]
	);

	// start bankid and get token
	const startBankID = useCallback(
		async payload => {
			let response;
			if (!payload?.orderRef) {
				// initialize state in case of reopening
				setBankIDTransaction({
					status: 'start',
					response: null
				});
				// send gtm event for bankid verification
				pushBankIDVerificationEvent(mode);

				const qrCodeMode = payload?.qrMode;

				response = await dispatch(getBankIDToken(false, payload));

				if (response?.orderRef) {
					setBankIDTransaction({
						status: 'start',
						response
					});

					if (!qrCodeMode && !isIntegrationTesting) {
						// get redirect url for bankid, also check if its ios auto redirect
						const url = getBankIDAppURL(response?.autoStartToken, response?.orderRef, mode);
						// skip checking this for cypress tests
						dispatch(disableValidateSession());
						window.location.href = url;
					}

					/*
					 * We avoid polling in iOS Safari due to issues with reload.
					 * However, we need to initiate polling in the following cases:
					 * 1. When qrCodeMode is enabled.
					 * 2. When running cypress tests (isIntegrationTesting), since it may also return "ios" which is actually cypress but is not affected by reload issues.
					 */
					if (qrCodeMode || !isIOSSafari() || isIntegrationTesting) {
						setPolling(somePolling => ({
							...somePolling,
							...{
								endTime: Date.now() + pollConfig.current.duration,
								on: true,
								failure: false
							}
						}));
					}
				} else {
					setBankIDTransaction({
						status: 'failure',
						response
					});

					// when the auth call fails.
					dispatch(bankIdError(response.bankidError, true, withinSidebar, null, false));
				}
			} else {
				// re-initiate bankid polling
				setBankIDTransaction({
					status: 'restart',
					response: null
				});
				const { orderRef: anOrderRef } = payload;
				dispatch(bankIdUpdateOrderRef(anOrderRef));
				setPolling(somePolling => ({
					...somePolling,
					...{
						endTime: Date.now() + pollConfig.current.duration,
						on: true,
						failure: false
					}
				}));
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[dispatch, isIntegrationTesting, mode, pollConfig.current]
	);

	const cancelPolling = useCallback(() => {
		clearTimeout(timer);
		setPolling({ endTime: 0, on: false, failure: true });
	}, [timer]);

	const cancelBankId = useCallback(
		async anOrderRef => {
			cancelPolling();
			if (!anOrderRef) {
				return null;
			}
			try {
				await cancelBankIDRequest(anOrderRef);
			} catch {
				return null;
			}
		},
		[cancelPolling]
	);

	// poll bankid for response
	const pollBankId = useCallback(
		async queryRef => {
			const ref = queryRef || orderRef;
			if (!ref || !polling.on) {
				return dispatch(clearBankID());
			}
			const googleID = getGoogleClientId();
			const franchiseID =
				GUEST_FRANCHISE_MODES.includes(mode) && guestFranchiseId > 0 ? guestFranchiseId : franchiseId;
			const requestType = getBankIDRequestType(mode);
			// by default let the status be pending to continue polling incases on exception
			let response = { bankidStatus: 'pending' };
			try {
				response = await bankIdResult({
					orderRef: ref,
					siteId,
					franchiseID,
					defaultLocale,
					selectedLocale,
					requestType,
					googleID
				});
			} catch (error) {
				// if polling throws an exception, poll again if its valid
				// else show a network error
				// need better message than general error BANJO-11954
				response.bankidError = 'BANKID_NETWORK_ERROR';
			}
			switch (response.bankidStatus) {
				case 'complete':
					if (response.success) {
						setBankIDTransaction({
							status: 'success',
							response
						});
					} else {
						setBankIDTransaction({
							status: 'failure',
							response
						});
					}
					return;
				// return resetBankIDUI(true);
				case 'pending':
					if (Number(new Date()) < polling.endTime && polling.on) {
						setBankIDTransaction({
							status: 'pending',
							response
						});
					} else {
						setBankIDTransaction({
							status: 'failure',
							response: response?.bankidError ?? {}
						});
					}
					return;
				case 'failed':
				default:
					setBankIDTransaction({
						status: 'failure',
						response
					});

				// resetBankIDUI();
			}
		},
		[
			orderRef,
			polling.on,
			polling.endTime,
			mode,
			guestFranchiseId,
			franchiseId,
			dispatch,
			siteId,
			defaultLocale,
			selectedLocale
		]
	);

	const reinitiateBankID = useCallback(
		async queryRef => {
			const ref = queryRef || orderRef;
			// reset transaction states
			setBankIDTransaction({
				status: 'restart',
				response: null
			});
			const googleID = getGoogleClientId();
			const franchiseID =
				GUEST_FRANCHISE_MODES.includes(mode) && guestFranchiseId > 0 ? guestFranchiseId : franchiseId;
			const requestType = getBankIDRequestType(mode);
			let response = { bankidStatus: 'pending' };

			try {
				response = await bankIdReinitiate({
					orderRef: ref,
					siteId,
					franchiseID,
					defaultLocale,
					selectedLocale,
					requestType,
					googleID
				});
				// add order ref back to redux just before response is handled, so that useEffects can run again
				dispatch(bankIdUpdateOrderRef(ref));
			} catch (error) {
				response.bankidError = 'BANKID_NETWORK_ERROR';
			}
			if (response.success) {
				setBankIDTransaction({
					status: 'success',
					response
				});
			} else {
				setBankIDTransaction({
					status: 'failure',
					response
				});
			}
			// dont need pending transaction state cause we only call fn once
		},
		[defaultLocale, dispatch, franchiseId, guestFranchiseId, mode, orderRef, selectedLocale, siteId]
	);

	// hook for handling bankid success
	useEffect(() => {
		if (!orderRef) {
			return;
		}
		const handleBankIDSuccess = async (response, anOrderRef) => {
			// set the session time
			setSessionTime();
			// if its registration dispatch these actions first
			dispatch(
				accountUpdate({
					isRegistration: response.isRegistration,
					id: response.playerId
				})
			);
			// lookup session to show next screens
			const sessionCriteria = await bankIdSessionLookup(anOrderRef);
			batch(() => {
				dispatch(
					bankIdUpdateSessionCriteria({
						shouldShowComplianceMessage: sessionCriteria.shouldShowComplianceMessage,
						shouldShowPrivacyPolicyMessage: sessionCriteria.shouldShowPrivacyPolicyMessage,
						shouldShowEmail: !sessionCriteria.isEmailSet,
						shouldShowPhone: !sessionCriteria.isMobileNumberSet,
						shouldShowDepositLimitPage: sessionCriteria.shouldShowDepositLimitPage,
						shouldShowWelcomePageDetails: sessionCriteria.shouldShowWelcomePageDetails,
						depositLimitCountryCode: sessionCriteria.depositLimitCountryCode
					})
				);
				dispatch(updateAcceptMarketingOffers(response.updateAcceptMarketingOffers));
			});
			// send analytics data
			postBankIDAnalytics({
				isRegistration: response.isRegistration,
				encryptedPID: response.playerId,
				qrMode
			});
			// send to mobile app
			if (response.isRegistration) {
				sendAppBridgeEvent(WEB_EVENTS.REGISTRATION_COMPLETE);
			}
			// actions if session criteria is fulfilled
			if (isSessionCriteriaFulfilled(sessionCriteria)) {
				// actions to dispatch based on session
				if (sessionCriteria.shouldShowWelcomePageDetails) {
					dispatch(showWelcomeScreen(true));
				}
				dispatch(enableMentorMessageDialog());
				await dispatch(validateSession({}, true));
			}
		};
		if (bankIDTransaction.status === 'success') {
			cancelPolling();
			handleBankIDSuccess(bankIDTransaction.response, orderRef);
		}
	}, [bankIDTransaction, dispatch, orderRef, bankIDTransaction.response, cancelPolling, qrMode]);

	// hook for handling bankid error
	useEffect(() => {
		if (!orderRef) {
			return;
		}
		const handleError = response => {
			// if account requires linkin
			if (showAccountLink(response.bankidError)) {
				if (isSwedishRegulatedFranchise) {
					batch(() => {
						dispatch(bankIdError(response.bankidError, true, withinSidebar, null, qrMode));
					});
				} else {
					batch(() => {
						dispatch(showUsernameAndPasswordLogin());
					});
				}
			}

			if (response.isOpenable) {
				// eslint-disable-next-line no-param-reassign
				response.errorCode = response.bankidError;
				// eslint-disable-next-line no-param-reassign
				response.result = {
					token: response.securityCode,
					email: response.email,
					playerId: response.playerId,
					onReopening: () => reinitiateBankID()
				};
				return dispatch({
					type: LOGIN_FAIL,
					payload: { ...response, withinSidebar }
				});
			}

			return dispatch(bankIdError(response.bankidError, true, withinSidebar, null, qrMode));
		};
		if (bankIDTransaction.status === 'failure') {
			cancelPolling();
			dispatch(clearBankID());
			handleError(bankIDTransaction.response, orderRef);
		}
	}, [
		bankIDTransaction,
		dispatch,
		orderRef,
		bankIDTransaction.response,
		startBankID,
		withinSidebar,
		isSwedishRegulatedFranchise,
		cancelPolling,
		qrMode,
		reinitiateBankID
	]);

	// hook for handlding bankid pending
	useEffect(() => {
		if (!orderRef) {
			return;
		}
		if (bankIDTransaction.status === 'pending') {
			const aTimer = setTimeout(() => {
				pollBankId(orderRef);
			}, pollConfig.current.interval);
			setTimer(aTimer);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bankIDTransaction.status, bankIDTransaction.response, pollBankId, orderRef, pollConfig.current]);

	// clear next timer after a transaction is resolved
	useEffect(() => {
		const { status } = bankIDTransaction;
		if (status === 'success' || status === 'failure') {
			clearTimeout(timer);
		}
	}, [bankIDTransaction.status, timer, bankIDTransaction]);

	// hook for intiating polling
	useEffect(() => {
		if (polling.on && previousPollingState !== polling.on) {
			setTimeout(() => {
				pollBankId();
			}, pollConfig.current.interval);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [polling.on, pollBankId, pollConfig.current.interval, previousPollingState]);

	useEffect(() => {
		return () => {
			cancelPolling();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		startBankID,
		bankIDTransaction,
		updateBankIDSession,
		cancelBankId,
		setPollConfig
	};
};

export default useBankID;
